/**
 * router.ts — the terrain cost field + deterministic A* road router (W2d-1 Task 1).
 *
 * This is the FOUNDATION of the adopted street-network spec
 * (docs/superpowers/specs/2026-07-08-street-network-design.md): it replaces the
 * vein-like space-colonization / streamline arterials with proper trunk-and-branch
 * PRIMARY roads routed by least-cost search over a terrain cost field. growth.ts
 * uses this router for regional primary and hamlet roads. Terrain probes also
 * check segment interiors; changes to this cost field can change generated graphs.
 *
 * COST FIELD (spec §"Terrain cost field", BINDING verbatim — per-step multiplier):
 *   - Slope, per degree over 5°           +8%/deg  (×(1 + 0.08·max(0,deg−5)))
 *   - Slope over 20°                       ×4       (stacks on the above → ~×8.8 at
 *                                                    20°, "effectively forbidden",
 *                                                    forces switchbacks/detours)
 *   - Water encountered along the step    ×6       (bridges rare + load-bearing)
 *   - Contour bonus (heading ≤15° of iso)  ×0.85
 *   - Existing-road reuse                   ×0.4    (the load-bearing knob: later
 *                                                    primaries MERGE into earlier
 *                                                    ones → trunk-and-branch, not a
 *                                                    spiderweb)
 * The base of a step is its horizontal chart run in METRES, so a flat, dry,
 * off-contour, un-reused street costs exactly its length. Every factor above is a
 * dimensionless multiplier on that length.
 *
 * DETERMINISM. The router draws NO randomness whatsoever (like the old arterial
 * trace, it is a pure function of the terrain field + endpoints), so it registers
 * NO hash salt — the salt-registry gate is satisfied vacuously. All float math
 * routes through pmath / the spec-sanctioned Math.sqrt (pmath-ban gate covers this
 * file); no `**`. The lattice carries deterministic INTEGER ids; A* breaks ties by
 * a strict TOTAL order (f, then g, then lattice id ascending) and expands neighbours
 * in a FIXED direction order, so a route is byte-identical across runs and engines.
 *
 * TESTABILITY SEAM (documented deviation from the plan's `routeCost(sampler,…)` /
 * `routePrimary(sampler,…)` sketch — the established "signature adaptation"
 * pattern in this codebase, e.g. growth.ts's builder adaptation). The router
 * consumes a small {@link RouteTerrain} interface (height / water / isoline
 * direction) rather than a raw PlanetSampler. {@link makeSamplerTerrain} adapts a
 * real PlanetSampler + RegionFrame into that interface using EXACTLY the binding
 * sampler conventions — heights sampled at the real direction (climate off), the
 * `seaRel !== 0` no-sea guard on the water test, and fields.ts's `arterialDirAt`
 * contour machinery projected onto the chart. Synthetic terrain (a steep wall, a
 * strait, a flat plain) is then a few lines of pure math in the oracle, so the cost
 * field is tested against terrain DIRECTLY, independent of world tuning — the same
 * philosophy as growth.ts's synthetic-seed oracles.
 */
import * as pmath from "../pmath.js";
import type { PlanetSampler, TerrainSample } from "../universe.js";
import { type RegionFrame, localToDir } from "./citygraph.js";
import { arterialDirAt } from "./fields.js";

const RAD2DEG = 180 / Math.PI; // Math.PI is not a banned intrinsic; portable constant.

// ── cost-field parameters (spec table, binding; culture-tunable later) ─────────

/**
 * The tier-invariant terrain cost multipliers (spec §"Terrain cost field"). Held
 * as data so Task 2+ can blend per-culture knobs; the defaults are the spec values
 * verbatim and MUST NOT drift without measured escalation.
 */
export interface RouteCostParams {
  /** Slope below this many degrees is free (no slope penalty). Spec: 5°. */
  slopeFlatDeg: number;
  /** Extra cost fraction per degree of slope above {@link slopeFlatDeg}. Spec: 0.08. */
  slopePerDegOver5: number;
  /** Slope above this many degrees triggers the hard multiplier. Spec: 20°. */
  slopeSteepDeg: number;
  /** Multiplier stacked on when slope exceeds {@link slopeSteepDeg}. Spec: ×4. */
  slopeSteepMult: number;
  /** Flat multiplier when any sampled part of a step is water. */
  waterMult: number;
  /** Half-angle (degrees) within which a step counts as contour-following. Spec: 15°. */
  contourWithinDeg: number;
  /** Bonus multiplier for a contour-following step. Spec: ×0.85. */
  contourMult: number;
  /** Discount multiplier when the step reuses an existing road corridor. Spec: ×0.4. */
  reuseMult: number;
  /** ON-ROAD band (m): the reuse discount fires only within this of an existing
   *  polyline (R3 braid fix — at the old 20 m radius the ADJACENT lattice line
   *  earned the discount, so parallel strands rode beside a trunk for free and
   *  multi-anchor cells braided; measured 28–42 parallel primary pairs/cell). */
  reuseOnM: number;
  /** BESIDE-ROAD corridor (m): steps near-but-not-on an existing road cost extra. */
  braidNearM: number;
  /** Corridor surcharge — join the trunk or route genuinely elsewhere. */
  braidMult: number;
}

/** The spec's binding cost table, verbatim. */
export const DEFAULT_ROUTE_COST: RouteCostParams = {
  slopeFlatDeg: 5,
  slopePerDegOver5: 0.08,
  slopeSteepDeg: 20,
  slopeSteepMult: 4,
  waterMult: 6,
  contourWithinDeg: 15,
  contourMult: 0.85,
  reuseMult: 0.4,
  reuseOnM: 7,
  braidNearM: 26,
  braidMult: 1.6,
};

/** Default lattice spacing (metres) in the region chart (spec: ~20 m). */
export const LATTICE_M = 20;
/** Maximum spacing of road centreline terrain probes (not a full-width corridor test). */
export const ROAD_PROBE_M = 5;
const MAX_ROAD_PROBES = 4096;

/** Shared by routing and bridge emission, so a wet interior cannot become a dry road. */
export function segmentHasWater(
  isWater: (u: number, v: number) => boolean,
  au: number, av: number, bu: number, bv: number,
): boolean {
  const steps = Math.max(1, Math.ceil(pmath.hypot(bu - au, bv - av) / ROAD_PROBE_M));
  if (!Number.isFinite(steps) || steps > MAX_ROAD_PROBES) return true;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    if (isWater(au + (bu - au) * t, av + (bv - av) * t)) return true;
  }
  return false;
}

/** Worst sampled grade prevents equal endpoint heights from hiding a ridge or ditch. */
function segmentGrade(terrain: RouteTerrain, au: number, av: number, bu: number, bv: number): number {
  const run = pmath.hypot(bu - au, bv - av);
  const steps = Math.max(1, Math.ceil(run / ROAD_PROBE_M));
  if (!Number.isFinite(steps) || steps > MAX_ROAD_PROBES) return Infinity;
  let prev = terrain.heightM(au, av), grade = 0;
  if (!Number.isFinite(prev)) return Infinity;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const height = terrain.heightM(au + (bu - au) * t, av + (bv - av) * t);
    if (!Number.isFinite(height)) return Infinity;
    if (run > 1e-9) grade = Math.max(grade, Math.abs(height - prev) / (run / steps));
    prev = height;
  }
  return grade;
}
/**
 * Default half-width (metres) of a primary corridor for the reuse test: a step
 * whose midpoint is within this of an existing net polyline earns the reuse
 * discount. ~one lattice cell, so a route running alongside the trunk snaps onto
 * it (trunk-and-branch) rather than merely brushing past.
 */
export const REUSE_RADIUS_M = 20;
/**
 * HARD node-expansion budget (spec §"Implementation warning" — A* budgets are hard
 * caps). Bounds the search on a ~region-sized lattice (a 2 km cell at 20 m spacing
 * is ~10 000 nodes; this ceiling permits generous detours + re-openings while
 * still terminating on a pathological case). Exhausting it returns null (see
 * {@link routePrimary}). Escalate WITH MEASUREMENTS if natural primaries truncate.
 */
export const MAX_EXPANSIONS = 200_000;

// ── the terrain abstraction (testability seam) ────────────────────────────────

/**
 * The minimal terrain view the cost field needs, over region-chart coordinates
 * (u,v) in metres. {@link makeSamplerTerrain} implements it from a real sampler;
 * oracles implement it with pure math.
 */
export interface RouteTerrain {
  /** Surface height at chart (u,v), in METRES. */
  heightM(u: number, v: number): number;
  /** True iff chart (u,v) is at/below sea level (with the no-sea seaRel guard). */
  isWater(u: number, v: number): boolean;
  /** Hard exclusion for lava; unlike water it cannot become an automatic bridge. */
  isBlocked?(u: number, v: number): boolean;
  /**
   * Unit isoline (contour) direction at chart (u,v), written to `out` as (du,dv)
   * in chart space; returns false when the contour degenerates (flat ground), in
   * which case the caller applies no contour bonus. Bidirectional: the sign is
   * irrelevant (the caller compares |cosθ|).
   */
  isoDir(u: number, v: number, out: [number, number]): boolean;
}

/**
 * Adapt a real PlanetSampler + RegionFrame into a {@link RouteTerrain}, honouring
 * the binding sampler conventions: heights at the real direction with climate off;
 * the `seaRel !== 0` no-sea guard on the water test (a no-sea planet has NO water —
 * its sub-datum lowlands are dry land); and fields.ts's `arterialDirAt` contour
 * field projected onto the chart basis (the growth.ts `contour2D` machinery,
 * replicated here to keep the router free of a growth.ts import).
 */
export function makeSamplerTerrain(sampler: PlanetSampler, frame: RegionFrame): RouteTerrain {
  const dir = new Float64Array(3);
  const tan = new Float64Array(3);
  const samp: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  const heightRel = (u: number, v: number): number => {
    localToDir(frame, u, v, dir);
    sampler.sample(dir[0], dir[1], dir[2], samp, false);
    return samp.h;
  };
  return {
    heightM(u, v) {
      return heightRel(u, v) * sampler.amplitude;
    },
    isWater(u, v) {
      if (sampler.seaRel === 0) return false; // no-sea planet: never water (seaRel guard)
      return heightRel(u, v) <= sampler.seaRel;
    },
    ...(sampler.def.archetype==='volcanic'?{isBlocked(u:number,v:number) {
      localToDir(frame,u,v,dir);
      sampler.sample(dir[0],dir[1],dir[2],samp,false,false,true);
      return (samp.lavaDepthM??0)>0;
    }}:{}),
    isoDir(u, v, out) {
      localToDir(frame, u, v, dir);
      arterialDirAt(sampler, dir[0], dir[1], dir[2], tan);
      const fu = frame.t1[0] * tan[0] + frame.t1[1] * tan[1] + frame.t1[2] * tan[2];
      const fv = frame.t2[0] * tan[0] + frame.t2[1] * tan[1] + frame.t2[2] * tan[2];
      const len = pmath.hypot(fu, fv);
      if (len < 1e-12) return false;
      out[0] = fu / len;
      out[1] = fv / len;
      return true;
    },
  };
}

// ── the cost field ────────────────────────────────────────────────────────────

/**
 * The BINDING per-step terrain cost of the lattice step (au,av)→(bu,bv), in
 * chart metres, per the spec table (slope + water + contour). The reuse discount
 * is NOT applied here — it depends on the accumulating {@link RoadNetwork} and is
 * applied by {@link routePrimary} — so this function is a pure function of terrain
 * alone and is directly unit-testable. Returns 0 for a degenerate (zero-length)
 * step. Never NaN for finite terrain.
 */
export function routeCost(
  terrain: RouteTerrain,
  params: RouteCostParams,
  au: number, av: number, bu: number, bv: number,
): number {
  return costWithGrade(terrain, params, au, av, bu, bv, segmentGrade(terrain, au, av, bu, bv));
}

function costWithGrade(
  terrain: RouteTerrain, params: RouteCostParams,
  au: number, av: number, bu: number, bv: number, grade: number,
): number {
  if (!Number.isFinite(grade)) return Infinity;
  const run = pmath.hypot(bu - au, bv - av);
  if (run < 1e-9) return 0;
  if(terrain.isBlocked&&segmentHasWater((u,v)=>terrain.isBlocked!(u,v),au,av,bu,bv))return Infinity;

  const slopeDeg = pmath.atan2(grade, 1) * RAD2DEG;
  let mult = 1 + params.slopePerDegOver5 * Math.max(0, slopeDeg - params.slopeFlatDeg);
  if (slopeDeg > params.slopeSteepDeg) mult *= params.slopeSteepMult;

  if (segmentHasWater((u, v) => terrain.isWater(u, v), au, av, bu, bv)) mult *= params.waterMult;

  // Contour bonus: heading within contourWithinDeg of the local isoline (either
  // way along the line). Compare |cosθ| ≥ cos(threshold) — no trig on the heading.
  const iso: [number, number] = [0, 0];
  if (terrain.isoDir((au + bu) / 2, (av + bv) / 2, iso)) {
    const su = (bu - au) / run, sv = (bv - av) / run;
    const cosAbs = Math.abs(su * iso[0] + sv * iso[1]);
    const cosThresh = pmath.cos(params.contourWithinDeg / RAD2DEG);
    if (cosAbs >= cosThresh) mult *= params.contourMult;
  }

  return run * mult;
}

// ── road network (reuse discount + spatial hash, rivers-pattern) ───────────────

/**
 * The accumulating set of routed primary polylines. Later primaries route through
 * a SHARED RoadNetwork so the reuse discount pulls them onto earlier trunks
 * (trunk-and-branch). Segments are smeared into a uniform chart-grid spatial hash
 * (the rivers/carveindex pattern) so {@link nearPolyline} answers "is this point
 * within r of any registered segment?" with a single local bucket scan.
 *
 * DETERMINISM: {@link nearPolyline} tests set MEMBERSHIP only (a boolean by
 * distance), never enumerates buckets in order, so registration order cannot leak
 * into a query result.
 */
export class RoadNetwork {
  private buckets = new Map<string, number[]>();
  private segs: number[] = []; // flat [u0,v0,u1,v1, u0,v0,u1,v1, …]
  constructor(private readonly cellM: number = REUSE_RADIUS_M) {}

  private key(gx: number, gy: number): string {
    return gx + "," + gy;
  }

  /** Register a polyline (>=1 point); each consecutive pair becomes a segment. */
  register(points: { u: number; v: number }[]): void {
    for (let i = 0; i + 1 < points.length; i++) {
      this.addSeg(points[i].u, points[i].v, points[i + 1].u, points[i + 1].v);
    }
  }

  private addSeg(u0: number, v0: number, u1: number, v1: number): void {
    const idx = this.segs.length / 4;
    this.segs.push(u0, v0, u1, v1);
    // Smear into every bucket the segment's bounding box touches, so a query near
    // any point of the segment finds it (see nearPolyline's coverage argument).
    const gx0 = Math.floor(Math.min(u0, u1) / this.cellM);
    const gx1 = Math.floor(Math.max(u0, u1) / this.cellM);
    const gy0 = Math.floor(Math.min(v0, v1) / this.cellM);
    const gy1 = Math.floor(Math.max(v0, v1) / this.cellM);
    for (let gx = gx0; gx <= gx1; gx++) {
      for (let gy = gy0; gy <= gy1; gy++) {
        const k = this.key(gx, gy);
        let b = this.buckets.get(k);
        if (b === undefined) { b = []; this.buckets.set(k, b); }
        b.push(idx);
      }
    }
  }

  /** Number of registered segments (test/introspection support). */
  get segmentCount(): number {
    return this.segs.length / 4;
  }

  /**
   * True iff chart (u,v) lies within `r` metres of any registered segment. Scans
   * buckets overlapping the query's r-disc bounding box; a point within r of a
   * segment has its closest segment-point inside that box, and that box's bucket
   * carries the segment (registered across its whole bbox), so coverage holds for
   * any cellM.
   */
  nearPolyline(u: number, v: number, r: number): boolean {
    const gx0 = Math.floor((u - r) / this.cellM);
    const gx1 = Math.floor((u + r) / this.cellM);
    const gy0 = Math.floor((v - r) / this.cellM);
    const gy1 = Math.floor((v + r) / this.cellM);
    const r2 = r * r;
    for (let gx = gx0; gx <= gx1; gx++) {
      for (let gy = gy0; gy <= gy1; gy++) {
        const b = this.buckets.get(this.key(gx, gy));
        if (b === undefined) continue;
        for (let j = 0; j < b.length; j++) {
          const s = b[j] * 4;
          if (segDist2(u, v, this.segs[s], this.segs[s + 1], this.segs[s + 2], this.segs[s + 3]) <= r2) return true;
        }
      }
    }
    return false;
  }
}

/** Squared distance from point (px,py) to segment (ax,ay)-(bx,by). */
function segDist2(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax, dy = by - ay;
  const l2 = dx * dx + dy * dy;
  let t = l2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + dx * t, cy = ay + dy * t;
  const ex = px - cx, ey = py - cy;
  return ex * ex + ey * ey;
}

// ── A* over the hashed lattice ─────────────────────────────────────────────────

/** Options for {@link routePrimary} (all bounded + deterministic). */
export interface RouteOpts {
  /** HARD grade wall (rise/run): lattice steps steeper than this are FORBIDDEN
   *  outright, not just ×4-costed (post-W2d-4 cliff fix — the soft penalty let
   *  ~0.1% of steps climb cliffs, and painted roads made every one visible).
   *  An explicit limit applies to EVERY sampled lattice segment, including the
   *  goal, with no ungated retry. Omitted: the legacy regional connectivity policy
   *  uses {@link ROAD_HARD_GRADE}, exempts the goal and retries ungated on failure.
   *  Exact endpoint connectors added by callers are outside this lattice API. */
  hardGrade?: number;
  /** Lattice spacing (metres). Default {@link LATTICE_M}. */
  latticeM?: number;
  /** Reuse-discount radius (metres). Default {@link REUSE_RADIUS_M}. */
  reuseRadiusM?: number;
  /** Cost-field parameters. Default {@link DEFAULT_ROUTE_COST}. */
  params?: RouteCostParams;
  /** Hard node-expansion budget. Default {@link MAX_EXPANSIONS}. */
  maxExpansions?: number;
  /**
   * Extra lattice margin (metres) around the endpoints' bounding box, so the
   * search may detour. Default `0.6·chord + 6·latticeM` — enough to route around a
   * modest obstacle; raise it for a case that must swing wide.
   */
  marginM?: number;
  /**
   * Optional HARD chart bound (metres): no lattice node with |u| or |v| beyond this
   * is expanded. Keeps a cell's primaries from routing DEEP into a neighbour cell's
   * world-space (the R1 D2 cross-cell weave source) — pass ≈ cell half-extent + a
   * small collar. A goal outside the bound (or reachable only by leaving it) returns
   * null (the caller's link fallback joins directly). Omitted ⇒ the default
   * endpoint-derived bound (no extra clip).
   */
  boundM?: number;
  /**
   * R1 review (border-approach reconciliation): an EXACT cell-confinement predicate.
   * When supplied, no interior lattice node whose world direction falls OUTSIDE the
   * route's own region cell is expanded — the route is confined to its cell's TRUE
   * world-space territory (the {@link boundM} chart clip is only an axis-aligned
   * approximation, and the true cell border is a curve well outside cellHalf+collar).
   * The GOAL is always reachable (a shared border PORT sits ON the border, so it may
   * dirToCell into the neighbour — it is exempt). Confining every primary to its own
   * cell means adjacent cells' roads can meet ONLY at shared border ports, so the
   * cross-cell union-planarity weaves (incl. the corner weaves the chart bound missed)
   * vanish BY CONSTRUCTION. Memoize it (one dirToCell per lattice node).
   */
  inCell?: (u: number, v: number) => boolean;
}

/** A routed primary, as a lattice polyline (chart metres). */
export interface RouteResult {
  points: { u: number; v: number }[];
  /** Total accumulated cost of the route (reuse-discounted). */
  cost: number;
  /** Node expansions consumed (for budget reporting). */
  expansions: number;
  /** True when the legacy connectivity retry ran without a grade wall. */
  usedGradeFallback: boolean;
}

// 8-connected neighbour offsets, in a FIXED order (E, NE, N, NW, W, SW, S, SE).
// Iterating this fixed order is part of the determinism contract.
const DIRS: readonly (readonly [number, number])[] = [
  [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1],
];

/** Min-heap entry. */
interface HeapNode { id: number; gi: number; gj: number; g: number; f: number; }

/** Deterministic total order: f asc, then g asc, then lattice id asc. */
function less(a: HeapNode, b: HeapNode): boolean {
  if (a.f !== b.f) return a.f < b.f;
  if (a.g !== b.g) return a.g < b.g;
  return a.id < b.id;
}

/**
 * Route a deterministic least-cost PRIMARY road from chart point `from` to `to`
 * over an 8-connected ~20 m lattice, minimising the spec cost field (slope / water
 * / contour) with the reuse discount applied for steps whose midpoint lies near an
 * existing `net` polyline. On success the polyline is REGISTERED into `net` (so
 * subsequent routes reuse it → trunk-and-branch) and returned.
 *
 * Returns null when the goal is unreachable (all corridors blocked within the
 * lattice) OR when the hard {@link RouteOpts.maxExpansions} budget is exhausted
 * first. DOCUMENTED BUDGET CHOICE: budget exhaustion yields null — a MISSING road
 * (honest: "no primary here") rather than a partial stub to nowhere, which would
 * read as broken geometry. Callers escalate the budget with measurements if real
 * primaries truncate.
 */
export function routePrimary(
  terrain: RouteTerrain,
  net: RoadNetwork,
  from: { u: number; v: number },
  to: { u: number; v: number },
  opts: RouteOpts = {},
): RouteResult | null {
  if (opts.hardGrade !== undefined && (!Number.isFinite(opts.hardGrade) || opts.hardGrade < 0)) return null;
  if (![from.u, from.v, to.u, to.v, opts.latticeM ?? LATTICE_M].every(Number.isFinite)
    || (opts.latticeM ?? LATTICE_M) <= 0) return null;
  // Grade-walled attempt first; ungated fallback keeps connectivity (see RouteOpts).
  const wall = opts.hardGrade ?? ROAD_HARD_GRADE;
  const gated = routePrimaryOnce(terrain, net, from, to, { ...opts, hardGrade: wall }, opts.hardGrade === undefined);
  if (gated !== null) return gated;
  if (opts.hardGrade !== undefined) return null;
  const fallback = routePrimaryOnce(terrain, net, from, to, { ...opts, hardGrade: undefined });
  if (fallback) fallback.usedGradeFallback = true;
  return fallback;
}

/** Hard grade wall default: tan 30° — steeper than any road, gentler than a cliff. */
export const ROAD_HARD_GRADE = 0.58;

function routePrimaryOnce(
  terrain: RouteTerrain,
  net: RoadNetwork,
  from: { u: number; v: number },
  to: { u: number; v: number },
  opts: RouteOpts = {},
  exemptGoalGrade = false,
): RouteResult | null {
  const L = opts.latticeM ?? LATTICE_M;
  void opts.reuseRadiusM; // superseded by params.reuseOnM/braidNearM (R3 braid fix)
  const params = opts.params ?? DEFAULT_ROUTE_COST;
  const maxExp = opts.maxExpansions ?? MAX_EXPANSIONS;
  const minMult = params.contourMult * params.reuseMult; // cheapest possible per-metre → admissible h

  const chord = pmath.hypot(to.u - from.u, to.v - from.v);
  const marginM = opts.marginM ?? (0.6 * chord + 6 * L);

  // Lattice half-span OFF: cover both endpoints + the detour margin. Nodes outside
  // [-OFF,OFF]² are never expanded, which (with maxExpansions) bounds the search.
  const maxAbs = Math.max(Math.abs(from.u), Math.abs(from.v), Math.abs(to.u), Math.abs(to.v));
  let OFF = Math.ceil((maxAbs + marginM) / L) + 1;
  // R1 D2: clip the lattice to a hard chart bound (cell + collar) when requested, so
  // primaries cannot detour DEEP into a neighbour cell. Never below what covers both
  // endpoints (else the goal is unreachable by construction).
  if (opts.boundM !== undefined) {
    const bOff = Math.ceil(opts.boundM / L) + 1;
    const need = Math.ceil(maxAbs / L) + 1;
    OFF = Math.min(OFF, Math.max(bOff, need));
  }
  const STRIDE = 2 * OFF + 1;
  const idOf = (gi: number, gj: number): number => (gi + OFF) * STRIDE + (gj + OFF);
  const inBounds = (gi: number, gj: number): boolean => gi >= -OFF && gi <= OFF && gj >= -OFF && gj <= OFF;

  const startGi = Math.round(from.u / L), startGj = Math.round(from.v / L);
  const goalGi = Math.round(to.u / L), goalGj = Math.round(to.v / L);
  const goalId = idOf(goalGi, goalGj);
  const goalU = goalGi * L, goalV = goalGj * L;

  const h = (gi: number, gj: number): number =>
    pmath.hypot(gi * L - goalU, gj * L - goalV) * minMult;

  const gScore = new Map<number, number>();
  const came = new Map<number, number>();
  const heap: HeapNode[] = [];
  const push = (n: HeapNode): void => {
    heap.push(n);
    let i = heap.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (less(heap[i], heap[p])) { const t = heap[i]; heap[i] = heap[p]; heap[p] = t; i = p; } else break;
    }
  };
  const pop = (): HeapNode => {
    const top = heap[0];
    const last = heap.pop()!;
    if (heap.length > 0) {
      heap[0] = last;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1, r = 2 * i + 2;
        let m = i;
        if (l < heap.length && less(heap[l], heap[m])) m = l;
        if (r < heap.length && less(heap[r], heap[m])) m = r;
        if (m === i) break;
        const t = heap[i]; heap[i] = heap[m]; heap[m] = t; i = m;
      }
    }
    return top;
  };

  const startId = idOf(startGi, startGj);
  gScore.set(startId, 0);
  push({ id: startId, gi: startGi, gj: startGj, g: 0, f: h(startGi, startGj) });

  let expansions = 0;
  while (heap.length > 0) {
    const cur = pop();
    // Skip stale heap entries (a better g was recorded after this was pushed).
    const best = gScore.get(cur.id);
    if (best === undefined || cur.g > best) continue;

    if (cur.id === goalId) {
      return finish(cur.g, expansions);
    }

    expansions++;
    if (expansions > maxExp) return null; // HARD budget → honest null (documented)

    for (let d = 0; d < DIRS.length; d++) {
      const ngi = cur.gi + DIRS[d][0];
      const ngj = cur.gj + DIRS[d][1];
      if (!inBounds(ngi, ngj)) continue;
      const nid = idOf(ngi, ngj);
      const au = cur.gi * L, av = cur.gj * L;
      const bu = ngi * L, bv = ngj * L;
      // R1 review: EXACT cell confinement — never route through a lattice node that
      // belongs to a neighbour cell, NOR take a step whose MIDPOINT bows across the
      // (curved) border into a neighbour (the goal port is exempt). Keeps every primary
      // — nodes AND segments — in its own world-space territory, so adjacent cells' roads
      // meet only at shared ports and cannot weave, even near a shared border corner.
      if (opts.inCell !== undefined && nid !== goalId) {
        // Every non-goal STEP must stay in-cell (endpoint + interior samples at ¼,½,¾),
        // so a straight chord cannot bow across the curved border into a neighbour
        // between lattice nodes. The GOAL step is fully exempt — a shared port sits ON
        // the border (its own cell may be the neighbour), and a through-road / port
        // approach must be able to reach it (else hamlet through-roads + port approaches
        // are dropped, violating connectivity + the through-road target).
        let out2 = !opts.inCell(bu, bv);
        if (!out2) for (const tt of [0.25, 0.5, 0.75]) { if (!opts.inCell(au + (bu - au) * tt, av + (bv - av) * tt)) { out2 = true; break; } }
        if (out2) continue;
      }
      const grade = segmentGrade(terrain, au, av, bu, bv);
      if (!Number.isFinite(grade)) continue;
      if (opts.hardGrade !== undefined && !(exemptGoalGrade && nid === goalId)
        && grade > opts.hardGrade) continue;
      let step = costWithGrade(terrain, params, au, av, bu, bv, grade);
      if (!Number.isFinite(step)) continue;
      const midU = (au + bu) / 2, midV = (av + bv) / 2;
      // R3 braid fix: the ×reuseMult trunk discount fires ONLY when the step is
      // truly ON an existing road; a step in the beside-road corridor pays a
      // surcharge instead — later primaries MERGE into trunks, never ride beside.
      if (net.nearPolyline(midU, midV, params.reuseOnM)) step *= params.reuseMult;
      else if (net.nearPolyline(midU, midV, params.braidNearM)) step *= params.braidMult;
      const ng = cur.g + step;
      const prev = gScore.get(nid);
      if (prev === undefined || ng < prev) {
        gScore.set(nid, ng);
        came.set(nid, cur.id);
        push({ id: nid, gi: ngi, gj: ngj, g: ng, f: ng + h(ngi, ngj) });
      }
    }
  }
  return null; // unreachable within the lattice

  function finish(cost: number, exp: number): RouteResult {
    // Reconstruct the lattice polyline from the came-from map (goal → start).
    const ids: number[] = [];
    let id: number | undefined = goalId;
    while (id !== undefined) {
      ids.push(id);
      if (id === startId) break;
      id = came.get(id);
    }
    ids.reverse();
    const points = ids.map((pid) => {
      const gi = Math.floor(pid / STRIDE) - OFF;
      const gj = (pid % STRIDE) - OFF;
      return { u: gi * L, v: gj * L };
    });
    net.register(points);
    return { points, cost, expansions: exp, usedGradeFallback: false };
  }
}
